import { Link } from "@tanstack/react-router";
import { Instagram, Linkedin, Mail, Phone, Youtube } from "lucide-react";
import { HCA_PHONE_DISPLAY, HCA_PHONE_TEL } from "./public-actions";

const SOCIAL_LINKS = [
  {
    href: "https://www.linkedin.com/company/hcabrno/",
    label: "LinkedIn",
    Icon: Linkedin,
    slug: "linkedin",
  },
  {
    href: "https://www.instagram.com/hcaczechia",
    label: "Instagram",
    Icon: Instagram,
    slug: "instagram",
  },
  {
    href: "https://www.youtube.com/@HCACzechia_krelina",
    label: "YouTube",
    Icon: Youtube,
    slug: "youtube",
  },
] as const;

/** Patka veřejných stránek — legal info + odkazy na GDPR, O nás, kontakt. */
export function HcaPublicFooter() {
  return (
    <footer
      className="border-t border-border bg-[color:var(--hca-navy)] text-[color:var(--hca-ivory)]"
      data-testid="hca-public-footer"
    >
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 md:grid-cols-[1.4fr_1fr_1fr]">
        <div className="max-w-md space-y-2">
          <div className="flex items-center gap-3">
            <img
              src="/branding/HCA_Moravia_logo_navy.jpg"
              alt="HCA Moravia"
              className="h-14 w-auto rounded-md object-contain"
            />
          </div>
          <p className="text-sm text-[color:var(--hca-ivory)]/80">
            Praktické vzdělávání pro majitele, jednatele, ředitele a manažerské týmy, které chtějí
            zlepšit řízení lidí, obchod a výkon firmy.
          </p>
          <div className="mt-4 flex items-center gap-3" data-testid="hca-public-footer-social">
            {SOCIAL_LINKS.map(({ href, label, Icon, slug }) => (
              <a
                key={slug}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="text-[color:var(--hca-ivory)]/80 transition-colors hover:text-[color:var(--hca-gold)]"
                data-testid={`hca-public-footer-social-${slug}`}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>

        <nav className="grid gap-2 text-sm" aria-label="Odkazy v patičce">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-[color:var(--hca-gold)]">
            Web
          </div>
          <Link
            to="/o-nas"
            className="text-[color:var(--hca-ivory)]/80 hover:text-[color:var(--hca-gold)] hover:underline"
            data-testid="hca-public-footer-link-o-nas"
          >
            O nás
          </Link>
          <Link
            to="/terminy"
            className="text-[color:var(--hca-ivory)]/80 hover:text-[color:var(--hca-gold)] hover:underline"
            data-testid="hca-public-footer-link-terminy"
          >
            Termíny
          </Link>
          <Link
            to="/gdpr"
            className="text-[color:var(--hca-ivory)]/80 hover:text-[color:var(--hca-gold)] hover:underline"
            data-testid="hca-public-footer-link-gdpr"
          >
            GDPR
          </Link>
          <Link
            to="/obchodni-podminky"
            className="text-[color:var(--hca-ivory)]/80 hover:text-[color:var(--hca-gold)] hover:underline"
            data-testid="hca-public-footer-link-vop"
          >
            Obchodní podmínky
          </Link>
        </nav>

        <div className="space-y-3 text-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--hca-gold)]">
            Kontakt
          </div>
          <a
            href={`tel:${HCA_PHONE_TEL}`}
            className="flex items-center gap-2 text-[color:var(--hca-ivory)]/90 hover:text-[color:var(--hca-gold)]"
            data-testid="hca-public-footer-phone"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {HCA_PHONE_DISPLAY}
          </a>
          <a
            href="mailto:organizace@hcamoravia.cz"
            className="flex items-center gap-2 text-[color:var(--hca-ivory)]/90 hover:text-[color:var(--hca-gold)]"
            data-testid="hca-public-footer-email"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            organizace@hcamoravia.cz
          </a>
          <p className="text-[color:var(--hca-ivory)]/70">
            Úlehlova 20a, Brno-Líšeň, 628&nbsp;00
            <br />
            HCA Czechia s.r.o. · IČO: 23530936
            <br />
            DIČ: CZ23530936
          </p>
        </div>
      </div>

      <div className="border-t border-[color:var(--hca-ivory)]/10 bg-[color:var(--hca-navy)]/80 py-3">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-4 text-xs text-[color:var(--hca-ivory)]/70">
          <span>© {new Date().getFullYear()} HCA Czechia s.r.o.</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link
              to="/obchodni-podminky"
              className="hover:text-[color:var(--hca-gold)] hover:underline"
              data-testid="hca-public-footer-legal-vop"
            >
              Obchodní podmínky
            </Link>
            <Link
              to="/gdpr"
              className="hover:text-[color:var(--hca-gold)] hover:underline"
              data-testid="hca-public-footer-legal-gdpr"
            >
              Zásady ochrany osobních údajů
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
